import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { expVal } from '@confluence/feature-experiments';
import type {
	SpaceForBaseSpaceQueryType,
	SpaceForBaseSpaceQueryVariablesType,
} from '@confluence/load-edit-page/entry-points/LoadEditPageQueries.ts';
import { spaceForBaseSpaceQuery } from '@confluence/load-edit-page/entry-points/LoadEditPageQueries.ts';

import type {
	useSpaceBaseQuerySpaceQuery as useSpaceBaseQuerySpaceQueryType,
	useSpaceBaseQuerySpaceQueryVariables,
} from './__types__/useSpaceBaseQuerySpaceQuery.ts';

export const useSpaceBaseQuerySpaceQuery = gql`
	query useSpaceBaseQuerySpaceQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			key
			name
			alias
		}
	}
`;

export const useSpaceBaseQuery = ({ resolvedSpaceKey }: { resolvedSpaceKey: string }) => {
	const {
		data: spaceBaseQueryData,
		loading: spaceBaseQueryLoading,
		error: spaceBaseQueryError,
	} = useQuery<SpaceForBaseSpaceQueryType, SpaceForBaseSpaceQueryVariablesType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		spaceForBaseSpaceQuery,
		{
			skip:
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				Boolean(process.env.REACT_SSR) ||
				expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false),
			variables: { spaceKey: resolvedSpaceKey },
		},
	);

	const {
		data: singleSpaceQueryData,
		loading: singleSpaceQueryLoading,
		error: singleSpaceQueryError,
	} = useQuery<useSpaceBaseQuerySpaceQueryType, useSpaceBaseQuerySpaceQueryVariables>(
		useSpaceBaseQuerySpaceQuery,
		{
			skip:
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				Boolean(process.env.REACT_SSR) ||
				!expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false),
			variables: { spaceKey: resolvedSpaceKey },
		},
	);

	let space, loading;

	if (expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)) {
		loading = singleSpaceQueryLoading;
		space = singleSpaceQueryError && !singleSpaceQueryLoading ? null : singleSpaceQueryData?.space;
	} else {
		loading = spaceBaseQueryLoading;
		space =
			spaceBaseQueryError && !spaceBaseQueryLoading ? null : spaceBaseQueryData?.spaces?.nodes?.[0];
	}

	return { space, loading };
};
